import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAthleteMode } from "./AthleteModeContext";
import { downloadAthleteContract, fetchAthleteDeal, openAthleteThread, submitContent } from "./athleteServices";
import { EMPTY_VALUE, fmtMoney } from "./athleteUtils";
import { DELIVERABLE_STATUS, fmtDate, fmtDateTime, fmtLongRange } from "../../ForBrands/brandDealUtils";
import { usePhoneTheme } from "../../ForBrands/useIsPhone";
import TrackingMobile from "./mobile/TrackingMobile";
import {
  BackLink,
  Badge,
  BmModal,
  Btn,
  Card,
  DealStepper,
  ErrorState,
  Field,
  Input,
  InsightBox,
  Loading,
  Overline,
  TextArea,
} from "../../ForBrands/ui/BrandUI";

// Athlete Mode · campaign tracking (Figma "Campaign Tracking"): deal
// progress, contract management, deliverables with Submit → Log content,
// milestone payouts, the campaign brief, deal health and the activity log.
// On phones (the Athlete Mode phone theme) the same data renders as
// mobile/TrackingMobile (Figma 2000:1033), and Log content opens as a sheet.

const MAX_IMAGE_BYTES = 2 * 1024 * 1024;

const MILESTONE_BADGE = {
  released: { label: "Paid", dot: "#1f9d55", tone: "outline" },
  pending_approval: { label: "Pending", dot: "#edd200", tone: "outline" },
  scheduled: { label: "Upcoming", tone: "passive" },
};

const stepFor = (status) => (status === "completed" ? 4 : status === "signed" ? 3 : ["accepted", "contract_sent"].includes(status) ? 2 : 1);

const deliverableBadge = (d) => {
  const qty = d.quantity || 1;
  if (d.status === "approved") return { label: `${qty} of ${qty} Done`, tone: "outline", dot: "#1f9d55" };
  if (d.status === "under_review") return { label: "Under review", tone: "outline", dot: "#edd200" };
  if (d.approvedCount) return { label: `${d.approvedCount} of ${qty} Done`, tone: "passive" };
  const meta = DELIVERABLE_STATUS[d.status] || { label: d.status, tone: "passive" };
  return { label: meta.label, tone: "passive" };
};

// What a deliverable row shows, shared by the desktop table and the phone rows.
const deliverableRow = (d, active) => ({
  d,
  badge: deliverableBadge(d),
  canLog: active && !["locked", "approved"].includes(d.status) && d.status !== "under_review",
  revision: (d.submissions || []).find((s) => s.status === "revision_requested"),
  due: d.dueDate ? fmtDate(d.dueDate, { year: false }) : EMPTY_VALUE,
});

const milestoneLabel = (m) => (m.label || "").replace(/^on /, "").replace(/^\w/, (c) => c.toUpperCase());

// A dialog on tablets and desktops; in the phone theme BmModal opens it as a
// bottom sheet (primary on top) and the fields take the 56px phone size.
const LogContentModal = ({ deal, deliverable, onClose, onLogged }) => {
  const phone = usePhoneTheme();
  const fileRef = useRef(null);
  const [link, setLink] = useState("");
  const [caption, setCaption] = useState("");
  const [postedAt, setPostedAt] = useState(new Date().toISOString().slice(0, 10));
  const [image, setImage] = useState(null);
  const [busy, setBusy] = useState(false);
  const unit = (deliverable.approvedCount || 0) + (deliverable.submissions || []).filter((s) => s.status === "under_review").length + 1;

  const pickFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Attach an image screenshot (PNG or JPG).");
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      toast.error("Screenshots must be under 2 MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setImage({ name: file.name, data: reader.result });
    reader.readAsDataURL(file);
  };

  const submit = async () => {
    if (!link.trim() && !image) {
      toast.error("Add the post link or a screenshot as proof.");
      return;
    }
    setBusy(true);
    try {
      await submitContent(deal.id, {
        deliverableId: deliverable.id,
        link: link.trim(),
        caption: caption.trim(),
        postedAt,
        imageData: image?.data || "",
        tags: (caption.match(/#[\w]+/g) || []).slice(0, 6),
      });
      toast.success(`${deliverable.label}: content logged for review.`);
      onLogged();
    } catch (err) {
      toast.error(err.message || "Could not log the content");
    } finally {
      setBusy(false);
    }
  };

  return (
    <BmModal
      title="Log content"
      onClose={onClose}
      footer={
        <>
          <Btn variant="secondary" className="flex-1" onClick={onClose}>
            Cancel
          </Btn>
          <Btn className="flex-1" onClick={submit} disabled={busy}>
            {busy ? "Submitting..." : "Submit for review"}
          </Btn>
        </>
      }
    >
      <div className={phone ? "bm-phone-form flex flex-col gap-4" : "flex flex-col gap-4"}>
        <div className="rounded-[8px] bg-bm-subtle px-4 py-3">
          <Overline>{deliverable.label}</Overline>
          <div className="bm-body-m text-bm-ink">
            {deliverable.platform} · unit {Math.min(unit, deliverable.quantity || 1)} of {deliverable.quantity || 1}
            {deliverable.dueDate ? ` · due ${fmtDate(deliverable.dueDate)}` : ""}
          </div>
        </div>
        <Field label="Post link" hint="The public URL of the post, reel or video.">
          <Input value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://www.instagram.com/p/..." />
        </Field>
        <Field label="Caption" hint="Paste the caption so the brand can check hashtags and mentions.">
          <TextArea rows={3} value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="Summer training with @brand #SummerFit" />
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Posted on">
            <Input type="date" value={postedAt} onChange={(e) => setPostedAt(e.target.value)} />
          </Field>
          <Field label="Screenshot (proof)">
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={pickFile} />
            {phone ? (
              <Btn variant="secondary" size="lg" onClick={() => fileRef.current?.click()} className="w-full justify-start">
                <span className="min-w-0 truncate">{image ? image.name : "Attach screenshot"}</span>
              </Btn>
            ) : (
              <Btn variant="secondary" onClick={() => fileRef.current?.click()} className="justify-start">
                {image ? image.name : "Attach screenshot"}
              </Btn>
            )}
          </Field>
        </div>
        {image && <img src={image.data} alt="" className="max-h-[220px] w-full rounded-[8px] border border-bm-border object-cover" />}
      </div>
    </BmModal>
  );
};

const AthleteTrackingPage = () => {
  const { dealId } = useParams();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { refreshBadges } = useAthleteMode();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [logging, setLogging] = useState(null);
  const phone = usePhoneTheme();

  const load = async () => {
    try {
      const payload = await fetchAthleteDeal(dealId);
      setData(payload);
      setError(null);
      return payload;
    } catch (err) {
      setError(err.message || "Could not load this deal");
      return null;
    }
  };

  useEffect(() => {
    load().then((payload) => {
      const wanted = params.get("log");
      if (payload && wanted) {
        const d = (payload.deal.deliverables || []).find((x) => x.id === wanted);
        if (d && payload.deal.status === "signed" && d.status !== "locked" && d.status !== "approved") setLogging(d);
        setParams({}, { replace: true });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealId]);

  // Phones keep the stack header over the loading and error states so the
  // athlete can always go back.
  if (phone && (error || !data)) return <TrackingMobile error={error} onBack={() => navigate("/athlete/deals")} />;
  if (error) {
    return (
      <div className="py-10">
        <ErrorState>{error}</ErrorState>
      </div>
    );
  }
  if (!data) return <Loading />;

  const { deal, campaign, brand, athlete } = data;
  const offer = deal.offer || {};
  const contract = deal.contract;
  const active = deal.status === "signed";
  const payouts = deal.payouts || {};
  const remaining = (payouts.total || 0) - (payouts.released || 0) - (payouts.pending || 0);
  const signatures = [
    { name: `${brand.company} (Brand)`, at: contract?.brandSignedAt },
    { name: `${athlete.fullName} (Athlete)`, at: contract?.athleteSignedAt },
    contract?.guardian && { name: `${contract.guardian.name || contract.guardian.email} (Guardian)`, at: contract.guardian.signedAt },
  ].filter(Boolean);
  const signedCount = signatures.filter((s) => s.at).length;

  const message = async () => {
    try {
      const thread = await openAthleteThread(dealId);
      navigate(`/athlete/messages?thread=${thread.id}`);
    } catch (err) {
      toast.error(err.message || "Could not open the conversation");
    }
  };

  const download = async () => {
    try {
      await downloadAthleteContract(dealId, campaign?.name || "contract");
    } catch (err) {
      toast.error(err.message || "Could not download the contract");
    }
  };

  const stateLabel = active ? "Active deal" : deal.statusLabel;
  const canReviewContract = !active && ["accepted", "contract_sent"].includes(deal.status);
  const deliverables = (deal.deliverables || []).map((d) => deliverableRow(d, active));
  const milestones = deal.milestones?.length ? deal.milestones : (offer.milestones || []).map((m) => ({ ...m, status: "scheduled" }));
  const milestoneDue = (m) => (m.dueDate ? fmtDate(m.dueDate, { year: false }) : active ? EMPTY_VALUE : "on signature");
  const totals = [
    ["Total paid", payouts.released || 0],
    ["Pending", payouts.pending || 0],
    ["Remaining", Math.max(0, deal.milestones?.length ? remaining : Number(offer.compensation) || 0)],
    ["Total contract", Number(offer.compensation) || 0],
  ];
  const briefRows = [
    ["Campaign name", campaign?.name],
    ["Brand", brand.company],
    ["Campaign period", fmtLongRange(offer.startDate, offer.endDate)],
    ["Description", campaign?.description],
    ["Ad usage rights", offer.adUsageDays ? `${offer.adUsageDays} days` : null],
    ["Content guidelines", (offer.deliverables || []).map((r) => r.notes).filter(Boolean).join(" · ") || null],
  ].filter(([, v]) => v);
  const health = deal.health || (active ? "Your first deliverable is ready to log." : "Health tracking starts once the contract is signed.");
  const activity = (deal.activity || []).slice(0, 8);

  // The phone tree (Figma 2000:1033) reads the same values.
  const phoneView = phone && {
    campaignName: campaign?.name,
    company: brand.company,
    active,
    stateLabel,
    contextMeta: [brand.company, fmtLongRange(offer.startDate, offer.endDate)].filter(Boolean).join(" · "),
    headSub: `${brand.company}${
      contract?.athleteSignedAt && deal.status === "signed" ? ` · Active since ${fmtDate(offer.startDate || contract.athleteSignedAt)}` : ` · ${deal.statusLabel}`
    }`,
    canReviewContract,
    contractHref: `/athlete/deals/${dealId}/contract`,
    step: stepFor(deal.status),
    hasContract: Boolean(contract),
    contractHasFile: Boolean(contract?.hasFile),
    signatures: signatures.map((s) => ({ ...s, atText: s.at ? fmtDateTime(s.at) : "" })),
    signedCount,
    deliverables,
    noDeliverablesText: `Deliverables unlock once the contract is fully signed.${
      (offer.deliverableLines || []).length ? ` Agreed scope: ${offer.deliverableLines.join("; ")}.` : ""
    }`,
    totalValue: fmtMoney(offer.compensation),
    milestones: milestones.map((m) => ({
      id: m.id,
      label: milestoneLabel(m),
      due: milestoneDue(m),
      amount: fmtMoney(m.amount),
      status: m.status,
      statusLabel: (MILESTONE_BADGE[m.status] || MILESTONE_BADGE.scheduled).label,
    })),
    totals: totals.map(([label, value]) => [label, fmtMoney(value)]),
    brief: briefRows,
    health,
    activity: activity.map((a) => ({ id: a.id, date: fmtDate(a.at, { year: false }), text: a.text })),
  };

  // One root in both layouts with the Log content modal at the same place in
  // it, so a sheet that turns into a dialog (a phone rotated to landscape)
  // keeps what was typed in it.
  return (
    <div className={phone ? "flex flex-col" : "flex flex-col gap-6 pt-6"}>
      {phone ? (
        <TrackingMobile view={phoneView} onBack={() => navigate("/athlete/deals")} onMessage={message} onDownload={download} onLog={setLogging} />
      ) : (
        <>
          <BackLink to="/athlete/deals">Back to My Deals</BackLink>

          <div className="flex flex-wrap items-center justify-between gap-3 rounded-[12px] bg-bm-mutedbg px-6 py-4">
            <span className="flex items-center gap-3">
              <span className="bm-h-s text-bm-ink">{campaign?.name}</span>
              <span className="h-4 w-px bg-bm-strong" />
              <span className={`bm-label-l ${active ? "text-[#1f9d55]" : "text-bm-muted"}`}>{stateLabel}</span>
            </span>
            <span className="bm-body-m text-bm-muted">
              {brand.company} · {fmtLongRange(offer.startDate, offer.endDate)}
            </span>
          </div>

          <div className="flex flex-wrap items-end justify-between gap-4">
            <div className="flex flex-col gap-[6px]">
              <Overline brand>Campaign tracker</Overline>
              <h1 className="bm-h-l text-bm-ink">{campaign?.name}</h1>
              <p className="bm-body-l text-bm-muted">
                {brand.company}
                {contract?.athleteSignedAt && deal.status === "signed" ? ` · Active since ${fmtDate(offer.startDate || contract.athleteSignedAt)}` : ` · ${deal.statusLabel}`}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Btn variant="secondary" onClick={message}>
                Message {brand.company}
              </Btn>
              {canReviewContract && (
                <Btn onClick={() => navigate(`/athlete/deals/${dealId}/contract`)}>Review contract</Btn>
              )}
            </div>
          </div>

          <Card className="flex flex-col gap-4">
            <h2 className="bm-h-m text-bm-ink">Deal Progress</h2>
            <DealStepper current={stepFor(deal.status)} />
          </Card>

          <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
            <div className="flex min-w-0 flex-1 flex-col gap-6">
              {/* Contract management */}
              <Card className="flex flex-col gap-4">
                <h2 className="bm-h-m text-bm-ink">Contract Management</h2>
                <Overline>
                  Signatures · {signedCount} of {signatures.length} complete
                </Overline>
                <div className="grid gap-3 md:grid-cols-2">
                  {signatures.map((s) => (
                    <div key={s.name} className="rounded-[8px] bg-bm-subtle px-4 py-3">
                      <div className="bm-body-s text-bm-muted">{s.name}</div>
                      <div className="flex items-center gap-2 bm-label-l text-bm-ink">
                        <span className="h-[7px] w-[7px] rounded-full" style={{ background: s.at ? "#1f9d55" : "#edd200" }} />
                        {s.at ? `✓ Signed · ${fmtDateTime(s.at)}` : "Signature pending"}
                      </div>
                    </div>
                  ))}
                  {!signatures.length && <p className="bm-body-m text-bm-muted">The contract has not been sent yet.</p>}
                </div>
                {contract && (
                  <div className="flex flex-wrap items-center gap-3">
                    <Btn variant="secondary" size="sm" onClick={() => navigate(`/athlete/deals/${dealId}/contract`)}>
                      View signed contract
                    </Btn>
                    <Btn variant="secondary" size="sm" onClick={download}>
                      Download {contract.hasFile ? "PDF" : "terms"}
                    </Btn>
                  </div>
                )}
              </Card>

              {/* Deliverables */}
              <Card padding="p-0" className="overflow-hidden">
                <div className="px-6 pt-6">
                  <h2 className="bm-h-m text-bm-ink">Campaign Deliverables</h2>
                </div>
                <div className="overflow-x-auto px-6 pb-6">
                  <table className="w-full min-w-[640px]">
                    <thead>
                      <tr>
                        <th className="bm-th text-left !pl-0">Deliverable</th>
                        <th className="bm-th text-left">Platform</th>
                        <th className="bm-th text-left">Due date</th>
                        <th className="bm-th text-left">Status</th>
                        <th className="bm-th text-right !pr-0">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {deliverables.length === 0 && (
                        <tr>
                          <td colSpan={5} className="py-6 bm-body-m text-bm-muted">
                            Deliverables unlock once the contract is fully signed.
                            {(offer.deliverableLines || []).length ? ` Agreed scope: ${offer.deliverableLines.join("; ")}.` : ""}
                          </td>
                        </tr>
                      )}
                      {deliverables.map(({ d, badge, canLog, revision, due }) => {
                        return (
                          <tr key={d.id} className="border-t border-bm-border">
                            <td className="bm-td !pl-0">
                              <span className="flex flex-col">
                                <span className="bm-label-l text-bm-ink">{d.label}</span>
                                {revision && <span className="bm-body-s text-bm-critical">Revision requested{revision.note ? `: ${revision.note}` : ""}</span>}
                              </span>
                            </td>
                            <td className="bm-td text-bm-muted">{d.platform}</td>
                            <td className="bm-td text-bm-muted">{due}</td>
                            <td className="bm-td">
                              <Badge tone={badge.tone} dot={badge.dot}>
                                {badge.label}
                              </Badge>
                            </td>
                            <td className="bm-td !pr-0 text-right">
                              {d.status === "locked" ? (
                                <Btn variant="secondary" size="sm" disabled>
                                  Locked
                                </Btn>
                              ) : d.status === "approved" ? (
                                <span className="bm-body-s text-bm-muted">Approved</span>
                              ) : d.status === "under_review" ? (
                                <span className="bm-body-s text-bm-muted">In review</span>
                              ) : (
                                <Btn variant="secondary" size="sm" onClick={() => setLogging(d)} disabled={!canLog}>
                                  {revision ? "Resubmit" : "Submit"}
                                </Btn>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </Card>

              {/* Milestone payouts */}
              <Card className="flex flex-col gap-4">
                <div className="flex items-center justify-between">
                  <h2 className="bm-h-m text-bm-ink">Milestone Payouts</h2>
                  <span className="bm-h-xs text-bm-ink">Total Value: {fmtMoney(offer.compensation)}</span>
                </div>
                <div className="overflow-x-auto rounded-[8px] border border-bm-border">
                  <table className="w-full min-w-[560px]">
                    <thead>
                      <tr className="bg-bm-subtle">
                        <th className="bm-th pl-4 text-left">Milestone</th>
                        <th className="bm-th text-left">Amount</th>
                        <th className="bm-th text-left">Due date</th>
                        <th className="bm-th pr-4 text-left">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {milestones.map((m) => {
                        const meta = MILESTONE_BADGE[m.status] || MILESTONE_BADGE.scheduled;
                        return (
                          <tr key={m.id} className="border-t border-bm-border">
                            <td className="bm-td pl-4 text-bm-ink">{milestoneLabel(m)}</td>
                            <td className="bm-td bm-num-m text-bm-ink">{fmtMoney(m.amount)}</td>
                            <td className="bm-td text-bm-muted">{milestoneDue(m)}</td>
                            <td className="bm-td pr-4">
                              <Badge tone={meta.tone} dot={meta.dot}>
                                {meta.label}
                              </Badge>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="flex flex-wrap gap-8">
                  {totals.map(([label, value]) => (
                    <div key={label} className="flex flex-col">
                      <span className="bm-body-s text-bm-muted">{label}</span>
                      <span className="bm-num-m text-bm-ink">{fmtMoney(value)}</span>
                    </div>
                  ))}
                </div>
              </Card>
            </div>

            <div className="flex w-full flex-col gap-6 lg:w-[420px]">
              <Card className="flex flex-col gap-4">
                <h2 className="bm-h-m text-bm-ink">Campaign Brief</h2>
                {briefRows.map(([label, value]) => (
                  <div key={label} className="flex flex-col gap-[2px]">
                    <span className="bm-body-s text-bm-muted">{label}</span>
                    <span className="bm-body-m text-bm-ink">{value}</span>
                  </div>
                ))}
              </Card>

              <InsightBox title="Deal health insight">
                {health}
              </InsightBox>

              <Card className="flex flex-col gap-3">
                <Overline>Deal activity log</Overline>
                {activity.map((a) => (
                  <div key={a.id} className="flex items-start gap-3">
                    <span className="w-[44px] shrink-0 pt-[2px] bm-body-s text-bm-tertiary">{fmtDate(a.at, { year: false })}</span>
                    <span className="bm-body-m text-bm-ink">{a.text}</span>
                  </div>
                ))}
                {!(deal.activity || []).length && <p className="bm-body-m text-bm-muted">No activity yet.</p>}
              </Card>
            </div>
          </div>
        </>
      )}

      {logging && (
        <LogContentModal
          deal={deal}
          deliverable={logging}
          onClose={() => setLogging(null)}
          onLogged={async () => {
            setLogging(null);
            await load();
            refreshBadges();
          }}
        />
      )}
    </div>
  );
};

export default AthleteTrackingPage;
